import React, { useMemo, useState } from "react";
import {
  LayoutDashboard,
  ShieldCheck,
  FileText,
  User,
  HelpCircle,
  Phone,
} from "lucide-react";
import OverviewView from "./OverviewView";
import WarrantiesView from "./WarrantiesView";
import ClaimsView from "./ClaimsView";
import LegalView from "./LegalView";
import SupportView from "./SupportView";

// -----------------------------------------------------------------------------
// Mock Logged-in Customer + Data
// -----------------------------------------------------------------------------

const currentCustomer = {
  id: 501,
  name: "Rahul Sharma",
  email: "rahul.sharma@example.com",
  mobile: "+91 98765 12345",
  city: "Pune, Maharashtra",
  joinedOn: "2024-01-15",
};

// -----------------------------------------------------------------------------
// Mock Data – Warranties & Claims
// -----------------------------------------------------------------------------

const initialWarranties = [
  {
    id: "C-WAR-1001",
    productName: "Smartphone",
    deviceType: "Mobile Phone",
    model: "Galaxy A55 8GB/128GB Blue",
    brand: "Samsung",
    serialOrImei: "356789012345678",
    invoiceNo: "INV-2025-001",
    purchaseDate: "2025-08-15",
    purchaseFromType: "Retail Store",
    purchaseFromName: "Shree Mobile Store, Pune",
    purchaseChannel: "Retail Partner",
    warrantyPlan: "1 Year Extended Warranty",
    coverage:
      "Device manufacturing defects (power / motherboard / genuine display issues)",
    expiryDate: "2026-08-14",
    status: "Active",
  },
  {
    id: "C-WAR-1002",
    productName: "Tablet",
    deviceType: "Tablet",
    model: "Tab S9 6GB/128GB",
    brand: "Samsung",
    serialOrImei: "351234567890123",
    invoiceNo: "INV-2025-004",
    purchaseDate: "2025-10-20",
    purchaseFromType: "Online Portal",
    purchaseFromName: "GoElectronix Online",
    purchaseChannel: "Online",
    warrantyPlan: "6 Months Device Protection",
    coverage: "Device defects & touch / display / sound issues",
    expiryDate: "2026-04-20",
    status: "Active",
  },
  {
    id: "C-WAR-0998",
    productName: "Smartphone",
    deviceType: "Mobile Phone",
    model: "mPOS Wireless Demo Device",
    brand: "GoElectronix",
    serialOrImei: "352345678901234",
    invoiceNo: "INV-2024-220",
    purchaseDate: "2024-01-10",
    purchaseFromType: "Channel Partner",
    purchaseFromName: "ABC Tech Solutions",
    purchaseChannel: "Channel Partner",
    warrantyPlan: "1 Year Warranty",
    coverage: "Standard defects",
    expiryDate: "2025-01-09",
    status: "Expired",
  },
];

const initialClaims = [
  {
    id: "C-CLM-5001",
    warrantyId: "C-WAR-1001",
    productName: "Smartphone",
    issueSummary: "Device not powering on",
    raisedOn: "2025-10-10",
    status: "Approved",
    adminMessage: "Approved, pickup scheduled",
    lastUpdated: "2025-10-15",
    approvedAmount: 3500,
    payableAmount: 3000,
    payableStatus: "Paid",
  },
  {
    id: "C-CLM-5002",
    warrantyId: "C-WAR-0998",
    productName: "Smartphone",
    issueSummary: "Screen flickering",
    raisedOn: "2025-01-05",
    status: "Rejected",
    adminMessage: "Warranty expired.",
    lastUpdated: "2025-01-07",
    approvedAmount: 0,
    payableAmount: 0,
    payableStatus: "Not Applicable",
  },
];

// -----------------------------------------------------------------------------
// Format INR Currency
// -----------------------------------------------------------------------------



// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export default function CustomerWarrantyPortal() {
  const [view, setView] = useState("overview");
  const [warranties] = useState(initialWarranties);
  const [claims, setClaims] = useState(initialClaims);
  const [submittingClaim, setSubmittingClaim] = useState(false);


   const [claimForm, setClaimForm] = useState({
      warrantyId: "",
      issueSummary: "",
      extraDetails: "",
    });


  const stats = useMemo(() => {
    const active = warranties.filter((w) => w.status === "Active").length;
    const expired = warranties.filter((w) => w.status === "Expired").length;
    const total = warranties.length;

    const openClaims = claims.filter(
      (c) =>
        ["Pending", "In Progress", "Approved"].includes(c.status)
    ).length;

    return { active, expired, total, openClaims };
  }, [warranties, claims]);


   const handleSubmitClaim = (e) => {
    e.preventDefault();

    if (!claimForm.warrantyId || !claimForm.issueSummary) {
      alert("Please select a warranty and describe the issue.");
      return;
    }

    setSubmittingClaim(true);

    const selectedWarranty = warranties.find(
      (w) => w.id === claimForm.warrantyId
    );

    const newClaim = {
      id: `C-CLM-${5000 + claims.length + 1}`,
      warrantyId: claimForm.warrantyId,
      productName: selectedWarranty?.productName || "Unknown Product",
      issueSummary: claimForm.issueSummary,
      raisedOn: new Date().toISOString().slice(0, 10),
      status: "Pending",
      adminMessage: "Submitted. Our team will review your claim.",
      lastUpdated: new Date().toISOString().slice(0, 10),
      approvedAmount: 0,
      payableAmount: 0,
      payableStatus: "Pending",
    };

    setTimeout(() => {
      setClaims((prev) => [newClaim, ...prev]);
      setClaimForm({ warrantyId: "", issueSummary: "", extraDetails: "" });
      setSubmittingClaim(false);
    }, 400);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 flex flex-col text-slate-900">

      {/* NAV TABS */}
      <nav className="px-6 pt-3">
        <div className="flex bg-white/70 border p-1 rounded-2xl gap-1">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "warranties", label: "My Warranties", icon: ShieldCheck },
            { id: "claims", label: "Claims", icon: FileText },
            { id: "legal", label: "Legal & Policy", icon: HelpCircle },
            { id: "support", label: "Support", icon: Phone },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = view === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`px-3 py-1.5 rounded-xl flex items-center gap-1 text-xs ${
                  active
                    ? "bg-blue-600 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-3 h-3" /> {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* CONTENT */}
      <main className="flex-1 px-6 py-4">
        {view === "overview" && <OverviewView stats={stats} />}
        {view === "warranties" && <WarrantiesView warranties={warranties} claims={claims} />}
        {view === "claims" && <ClaimsView
            warranties={warranties}
            claims={claims}
            claimForm={claimForm}
            setClaimForm={setClaimForm}
            submitting={submittingClaim}
            onSubmit={handleSubmitClaim}
          />
          }
        {view === "legal" && <LegalView />}
        {view === "support" && <SupportView />}
      </main>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ✔ Placeholder Components (To Avoid Errors)
// -----------------------------------------------------------------------------


